import useInfiniteScrollTable from 'common/components/BasicTable/useInfiniteScrollTable'
import PageWrapper from 'common/components/PageWrapper'
import { CRUDAction, FeatureKey } from 'common/constants/permission'
import { useDefaultValueLabel } from 'common/utils/defaultValueLabel'
import { INITIAL_PAGE_SIZE, useCategory } from 'modules/inventory/api/queries'
import { Category } from 'modules/inventory/api/type'
import CategoryTools from 'modules/inventory/components/CategoryTools'
import CreateCategoryDialog from 'modules/inventory/pages/category/CreateCategoryPage'
import { formatThaiShortBE } from 'modules/inventory/helper/format_date'
import { usePermitSomeActionToFeature, usePermitToActionFeature } from 'modules/user/api/selector'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { PATH_DASHBOARD } from 'routes/paths'

import AddIcon from '@mui/icons-material/Add'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import LockIcon from '@mui/icons-material/Lock'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import {
  Button,
  Card,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material'

import NoDataNoBorder from 'components/NoDataNoBorder'

const CategoryPage = () => {
  const { t } = useTranslation(['category', 'warehouse'])
  const label = useDefaultValueLabel()
  const theme = useTheme()
  const navigate = useNavigate()
  const showAddCategory = usePermitToActionFeature([CRUDAction.Create], FeatureKey.Inventory)
  const canManageCategory = usePermitSomeActionToFeature(
    [CRUDAction.Update, CRUDAction.Delete],
    FeatureKey.Inventory
  )
  const [pageSize] = React.useState(INITIAL_PAGE_SIZE)
  const { data, isLoading, hasNextPage, fetchNextPage } = useCategory({ pageSize })
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [selectedRow, setSelectedRow] = useState<Category | null>(null)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)

  const handleScroll = useInfiniteScrollTable({
    isLoading,
    onLoading: () => { if (hasNextPage) fetchNextPage() },
  })

  const LINKS = [
    { name: t('warehouse:inventory.title'), href: '/dashboard/product' },
    { name: t('warehouse:inventory.settings.title'), href: '/dashboard/inventory/settings' },
    { name: t('title.category') },
  ]

  const handleOpenMenu = (e: React.MouseEvent<HTMLButtonElement>, row: Category) => {
    setAnchorEl(e.currentTarget)
    setSelectedRow(row)
  }

  const handleCloseMenu = () => {
    setAnchorEl(null)
    setSelectedRow(null)
  }

  const categories = data?.pages.flatMap((p) => p.categories || []).filter(Boolean) ?? []

  return (
    <PageWrapper
      title={
        <Stack direction="row" spacing={0.5} alignItems="center">
          <IconButton size="small" onClick={() => navigate(PATH_DASHBOARD.inventory.settings)}>
            <ArrowBackIosNewIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <Typography variant="h4">{t('title.category')}</Typography>
        </Stack>
      }
      titleHead={t('title.category')}
      links={LINKS}
      action={
        showAddCategory && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            {t('add.category')}
          </Button>
        )
      }
    >
      <Card
        sx={{
          width: '100%',
          bgcolor: 'common.white',
          borderRadius: '24px',
          p: 3,
        }}
      >
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          {t('category.detail')}
        </Typography>

        {isLoading ? (
          <NoDataNoBorder />
        ) : categories.length > 0 ? (
          <TableContainer
            component={Paper}
            onScroll={handleScroll}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            <Table
              stickyHeader
              sx={{
                '& thead th': {
                  fontSize: '14px',
                  color: theme.palette.common.black,
                  backgroundColor: '#F0F0F0',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                },
                '& tbody td': { borderBottom: '1px solid', borderColor: 'divider' },
                '& thead th:first-of-type': { borderTopLeftRadius: 12 },
                '& thead th:last-of-type': { borderTopRightRadius: 12 },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>{t('create.date')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('category.name')}</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {categories.map((row: Category, idx: number) => (
                  <TableRow key={row.id || idx} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {formatThaiShortBE(row.created_at)}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        {row.is_default && (
                          <LockIcon sx={{ fontSize: 16, color: 'success.main' }} />
                        )}
                        <Typography variant="body2">{label.inventoryCategory(row.name, row.is_default) || '-'}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      {!row.is_default && canManageCategory && (
                        <>
                          <IconButton size="small" onClick={(e) => handleOpenMenu(e, row)}>
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                          <CategoryTools
                            onClose={handleCloseMenu}
                            open={Boolean(anchorEl) && selectedRow?.id === row.id}
                            anchorEl={anchorEl}
                            categoryId={selectedRow?.id as string}
                            categoryItem={selectedRow as Category}
                          />
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <NoDataNoBorder />
        )}
      </Card>

      <CreateCategoryDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
      />
    </PageWrapper>
  )
}

export default CategoryPage
